import React, { useId, useState } from 'react';
import { Popover } from '@headlessui/react';
import { DayPicker } from 'react-day-picker';
import { CalendarIcon } from '@heroicons/react/24/outline';
import 'react-day-picker/dist/style.css';
import { cn } from '@/utils/cn';
import {
  parseDateTimeInput,
  parseDateInput,
  toDateTimeLocalValue,
  toDateValue,
  formatDateTimeDisplay,
  formatDateDisplay,
} from '@/utils/datetime';

const pad = (n: number) => String(n).padStart(2, '0');

export interface BaseDateTimeFieldProps {
  /** Committed value: 'YYYY-MM-DDTHH:mm' (datetime) or 'YYYY-MM-DD' (date), or ''. Omit for uncontrolled usage. */
  value?: string;
  /** Initial value for uncontrolled usage (FormData-based forms; pair with `name`). */
  defaultValue?: string;
  /** Called with the committed value on blur/Enter/picker selection. */
  onChange?: (value: string) => void;
  label?: string;
  id?: string;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  /** External validation error (takes precedence over the internal parse error). */
  error?: string;
  helpText?: string;
  placeholder?: string;
  min?: string;
  max?: string;
  className?: string;
  onBlur?: () => void;
}

interface InternalProps extends BaseDateTimeFieldProps {
  mode: 'date' | 'datetime';
}

/**
 * Typed-entry-first date/time field (VIS-136).
 *
 * The text input is the primary affordance: users type a date/time in any
 * common format and the value commits on blur or Enter — never left
 * uncommitted by an OS scroll-wheel picker. A native picker remains available
 * via the calendar button and commits immediately on selection.
 */
export const BaseDateTimeField: React.FC<InternalProps> = ({
  mode,
  value: valueProp,
  defaultValue,
  onChange,
  label,
  id,
  name,
  required,
  disabled,
  error,
  helpText,
  placeholder,
  min,
  max,
  className,
  onBlur,
}) => {
  const generatedId = useId();
  const inputId = id ?? `datetime-field-${generatedId}`;
  const [draft, setDraft] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  // Uncontrolled usage (FormData forms): track the committed value internally
  const [uncontrolledValue, setUncontrolledValue] = useState<string>(defaultValue ?? '');
  const value = valueProp ?? uncontrolledValue;

  const format = mode === 'datetime' ? formatDateTimeDisplay : formatDateDisplay;
  const parse = mode === 'datetime' ? parseDateTimeInput : parseDateInput;
  const toValue = mode === 'datetime' ? toDateTimeLocalValue : toDateValue;
  const defaultPlaceholder = mode === 'datetime' ? 'MM/DD/YYYY h:mm AM/PM' : 'MM/DD/YYYY';

  const shownText = isEditing ? draft : format(value);

  const commitValue = (committed: string) => {
    setUncontrolledValue(committed);
    onChange?.(committed);
  };

  const commit = () => {
    const text = draft.trim();
    setIsEditing(false);
    if (!text) {
      setParseError(null);
      commitValue('');
      return;
    }
    const parsed = parse(text);
    if (!parsed) {
      setParseError(
        mode === 'datetime'
          ? 'Unrecognized date/time — try MM/DD/YYYY h:mm AM/PM'
          : 'Unrecognized date — try MM/DD/YYYY'
      );
      return;
    }
    setParseError(null);
    commitValue(toValue(parsed));
  };

  const handleFocus = () => {
    setDraft(format(value));
    setIsEditing(true);
  };

  const handleBlur = () => {
    commit();
    onBlur?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      // Commit without submitting the surrounding form
      event.preventDefault();
      commit();
    }
  };

  // Popover picker draft. `pickerDay` is the selected calendar day; `pickerTime`
  // is the 'HH:mm' time-of-day (datetime mode only). Initialized from the
  // committed value when the popover opens; committed only on Save.
  const [pickerDay, setPickerDay] = useState<Date | undefined>();
  const [pickerTime, setPickerTime] = useState('09:00');

  const openPicker = () => {
    const parsed = value ? parse(format(value)) : null;
    setPickerDay(parsed ?? new Date());
    if (mode === 'datetime') {
      setPickerTime(parsed ? `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}` : '09:00');
    }
  };

  const savePicker = (close: () => void) => {
    if (!pickerDay) {
      close();
      return;
    }
    const result = new Date(pickerDay);
    if (mode === 'datetime') {
      const [h, m] = pickerTime.split(':').map((n) => parseInt(n, 10));
      result.setHours(Number.isFinite(h) ? h : 0, Number.isFinite(m) ? m : 0, 0, 0);
    }
    setParseError(null);
    setIsEditing(false);
    commitValue(toValue(result));
    close();
  };

  const minDate = min ? parse(min) ?? undefined : undefined;
  const maxDate = max ? parse(max) ?? undefined : undefined;

  const shownError = error ?? parseError;
  const hasError = !!shownError;

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label
          htmlFor={inputId}
          className="mb-2 block text-sm font-medium text-secondary-900 dark:text-secondary-100"
        >
          {label}
          {required && <span className="ml-1 text-danger-500">*</span>}
        </label>
      )}
      <div className="flex">
        {/* Canonical value for FormData-based forms; the visible text input
            holds the human display format */}
        {name && <input type="hidden" name={name} value={value} />}
        <input
          id={inputId}
          type="text"
          inputMode="text"
          autoComplete="off"
          value={shownText}
          placeholder={placeholder ?? defaultPlaceholder}
          disabled={disabled}
          aria-invalid={hasError || undefined}
          aria-describedby={shownError ? `${inputId}-error` : undefined}
          onFocus={handleFocus}
          onChange={(e) => {
            if (!isEditing) {
              setIsEditing(true);
            }
            setDraft(e.target.value);
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={cn(
            'h-10 w-full rounded-l-lg border bg-white px-3 py-2 text-sm placeholder:text-secondary-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-secondary-800 dark:placeholder:text-secondary-500',
            hasError
              ? 'border-danger-300 focus-visible:ring-danger-500 dark:border-danger-600'
              : 'border-secondary-300 dark:border-secondary-600'
          )}
        />
        {/* In-app picker popover (react-day-picker). Replaces the native OS
            datetime popup: the draft is held locally and committed only on
            Save, so there's no half-finished value and no click-outside-to-keep
            ambiguity. Cancel/Escape/outside-click discard. */}
        <Popover className="relative shrink-0">
          <Popover.Button
            type="button"
            disabled={disabled}
            onClick={openPicker}
            aria-label={label ? `Open date picker for ${label}` : 'Open date picker'}
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-r-lg border border-l-0 bg-secondary-50 text-secondary-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:bg-secondary-700 dark:text-secondary-300',
              hasError
                ? 'border-danger-300 dark:border-danger-600'
                : 'border-secondary-300 dark:border-secondary-600',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            <CalendarIcon className="h-5 w-5" aria-hidden="true" />
          </Popover.Button>
          <Popover.Panel className="absolute right-0 z-50 mt-2 rounded-lg border border-secondary-200 bg-white p-3 shadow-lg dark:border-secondary-700 dark:bg-secondary-800">
            {({ close }) => (
              <div className="flex flex-col gap-3">
                <DayPicker
                  mode="single"
                  selected={pickerDay}
                  onSelect={setPickerDay}
                  defaultMonth={pickerDay}
                  fromDate={minDate}
                  toDate={maxDate}
                  showOutsideDays
                  className="vf-daypicker"
                />
                {mode === 'datetime' && (
                  <label className="flex items-center justify-between gap-2 text-sm text-secondary-700 dark:text-secondary-200">
                    Time
                    <input
                      type="time"
                      value={pickerTime}
                      onChange={(e) => setPickerTime(e.target.value)}
                      className="h-9 rounded-md border border-secondary-300 bg-white px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-secondary-600 dark:bg-secondary-700"
                    />
                  </label>
                )}
                <div className="flex justify-end gap-2 border-t border-secondary-100 pt-3 dark:border-secondary-700">
                  <button
                    type="button"
                    onClick={() => close()}
                    className="rounded-md px-3 py-1.5 text-sm font-medium text-secondary-600 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => savePicker(close)}
                    disabled={!pickerDay}
                    className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Save
                  </button>
                </div>
              </div>
            )}
          </Popover.Panel>
        </Popover>
      </div>
      {(shownError ?? helpText) && (
        <div className="mt-1 text-xs">
          {shownError ? (
            <p id={`${inputId}-error`} className="text-danger-600 dark:text-danger-400">
              {shownError}
            </p>
          ) : (
            <p className="text-secondary-500 dark:text-secondary-400">{helpText}</p>
          )}
        </div>
      )}
    </div>
  );
};
